<template>
    <div class="tertiary-button-wrapper">
        <!-- Label -->
        <label-generic
        v-if="topLabel"
        :text="topLabel"/>
		<!-- Button -->
        <button
        :id="id"
        class="button-tertiary"
        :disabled="disabled"
        :class="[
            { 'icon-only': !label },
            { 'with-external-label': withExternalLabel },
            { 'error': hasError },
            buttonStyle,
            buttonSize,
			getAppThemeMode
        ]"
        @click="clickHandler">
            <!-- Button Icon -->
            <app-icon v-if="icon" class="icon"/>
            <!-- Button label -->
            <div v-if="label && !withExternalLabel" class="label">{{ label }}</div>
        </button>
        <!-- External Label -->
        <div v-if="label && withExternalLabel" class="external-label" :class="{ 'disabled': disabled }">
            {{ label }}
        </div>
    </div>
</template>

<script setup>
import LabelGeneric from '@/components/generic/labels/Generic'

/* Vue */
import { computed, defineAsyncComponent } from 'vue'
import { iconModules } from '@/assets/icons/iconModules'
/* Store */
import { storeToRefs } from 'pinia'
import { useProjectBaseStore } from '@/store/projectBase'
const { getAppThemeMode } = storeToRefs(useProjectBaseStore())

/* Emmitters */
const emit = defineEmits(['clicked'])

/** Props received:
@label {String}
@icon {String}
@buttonStyle {String} can be [square (default), square-transparent, round, round-transparent]
@buttonSize {String} can be [medium (default), small, tiny]
@withExternalLabel {Boolean} to add label below button
@disabled {Boolean}
@hasError {Boolean}
@topLabel {String} - if you need to display a top label
@allowEventPropagation {Boolean} for when we want the click event to bubble up
*/
const props = defineProps({
    id: String,
    label: String,
	icon: String,
	buttonStyle: {
        type: String,
        default: 'square'
    },
    buttonSize: {
        type: String,
        default: 'medium'
    },
    withExternalLabel: Boolean,
    disabled: Boolean,
    hasError: Boolean,
    topLabel: String,
	allowEventPropagation: Boolean
})

/* Computed */
const appIcon = computed(() => {
    const icon = props.icon
	return defineAsyncComponent(iconModules[`/src/assets/icons/${icon}.svg`])
})

const clickHandler = $event => {
	if (!props.allowEventPropagation) $event.stopPropagation()
	emit('clicked')
}
</script>

<style lang="scss" scoped>
.tertiary-button-wrapper {
    width: fit-content;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;

    .button-tertiary {
        width: 100%;
        padding: 0 16px;

        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;

        @include H7-Medium;
        color: var(--secondary-dark-2);
        background-color: var(--secondary-light-4);
        border: 1px solid var(--secondary-light-1);

        cursor: pointer;

        svg.icon {
            flex-shrink: 0;

            width: 16px;
            height: 16px;
            pointer-events: none;

            :deep(.change-color-fill) {
                fill: var(--secondary-dark-2);
            }
        }

        .label {
            text-align: left;
        }

        /* Style classes */
        &.square, &.square-transparent {
            border-radius: 8px;
        }

        &.round, &.round-transparent {
            border-radius: 20px;

            &.icon-only,
            &.with-external-label {
                border-radius: 50%;
            }
        }

		&.round-transparent, &.square-transparent {
			color: var(--secondary-light-4);
			background-color: transparent;
			border: 1px solid var(--button-tertiary-round-transparent-border);

			svg.icon {
				:deep(.change-color-fill) {
					fill: var(--secondary-light-4);
				}
			}

			&.dark {
				color: var(--secondary-dark-3);

				svg.icon {
					:deep(.change-color-fill) {
						fill: var(--secondary-dark-3);
					}
				}
			}
        }

        /* Size classes */
        &.medium {
            height: 48px;
        }

        &.small {
            height: 40px;

            &.icon-only,
            &.with-external-label {
                width: 40px;
            }
        }

        &.tiny {
            height: 32px;

            padding: 0 8px;

            &.icon-only {
                width: 32px;
            }
        }

        &.error {
            border: 1px solid var(--negative);
        }

        @media (hover: hover) and (pointer: fine) {
            &:hover {
                background-color: var(--button-secondary-state-hover);
            }
        }

        &:active {
            background-color: var(--button-secondary-state-press);
        }

		&.round-transparent, &.square-transparent {
			@media (hover: hover) and (pointer: fine) {
				&:hover {
					background-color: var(--button-tertiary-transparent-state-hover);
				}
			}

			&:active {
				background-color: var(--button-tertiary-transparent-state-press);
			}
        }

        &:disabled {
            cursor: default;
            pointer-events: none;
            opacity: 0.24;
        }
    }

    .external-label {
        @include H7-Medium;
        line-height: 16px;

        color: var(--secondary-dark-1);

        &.disabled {
            opacity: 0.24;
        }
    }
}
</style>
